import type { SettlementPosition } from '@gatecheckpro/contracts';
import { Notice, formatMoney } from '@gatecheckpro/design-system';

/**
 * An organizer's money, shown as the components it is made of.
 *
 * The temptation this file exists to resist is the single number. "You are owed
 * ₦4,120,000" is what an organizer wants to read and is the one thing this
 * screen must not say, because it is not a fact the platform holds. What the
 * ledger knows is what was collected, what went back, and what has been
 * promised back; what CABS has not decided is who bears the provider's charge,
 * what commission applies, what tax is withheld, or when anything is paid.
 *
 * So the breakdown shows the parts and names the gap. An organizer who reads
 * this and asks "so when do I get it?" has understood the screen correctly.
 *
 * Every line here is a sum of postings that exist. Nothing on this screen is
 * computed in the browser — not a subtotal, not a difference, not a percentage.
 * A client that did arithmetic on money would be a second accounting authority,
 * and the first time it disagreed with the ledger nobody would know which was
 * right.
 */

/**
 * What is not on this screen, and why, stated to the reader rather than only in
 * a comment.
 *
 * Absent components are absent from the contract too: `SettlementPosition` has
 * no field for commission, tax or reserve, and inventing a row that renders
 * "—" would imply the platform is tracking something it is not. Silence about
 * a deduction that has never been configured is more honest than a placeholder
 * that looks like a pending calculation.
 */
export const SETTLEMENT_NOT_A_PAYOUT =
  'This is what the ledger records, not a payout. No commission, tax or reserve has been ' +
  'applied, and no payment is scheduled from this screen.';

export interface SettlementLine {
  readonly label: string;
  readonly meaning: string;
  readonly amountMinor: number;
  readonly currency: string;
  /** Reported rather than deducted. Rendered outside the running total. */
  readonly informationalOnly?: boolean;
}

/**
 * The position as lines, in the order somebody reads them.
 *
 * A pure function so the ordering and the wording can be asserted without
 * rendering, and so no component is tempted to assemble its own version.
 */
export function settlementLines(position: SettlementPosition): SettlementLine[] {
  const { currency } = position;
  return [
    {
      label: 'Collected',
      meaning: 'Payments the provider confirmed. Gross — nothing has been taken off.',
      amountMinor: position.grossCollectedMinor,
      currency,
    },
    {
      label: 'Refunded',
      meaning: 'Refunds that have actually settled with the provider.',
      amountMinor: -position.refundedMinor,
      currency,
    },
    {
      label: 'Refunds in flight',
      meaning: 'Approved and not yet settled. This money is already promised back.',
      amountMinor: -position.refundsInFlightMinor,
      currency,
    },
    {
      /*
       * Reported, never subtracted.
       *
       * Who bears a provider's charge is a commercial question CABS has not
       * answered. Subtracting it here would answer it by arithmetic — inventing
       * the policy rather than surfacing the evidence — and an organizer would
       * reasonably read the result as a decision that had been made.
       */
      label: 'Provider charges observed',
      meaning:
        'What the payment provider charged on these collections. Shown because it is real, ' +
        'not deducted because who bears it has not been decided.',
      amountMinor: position.observedProviderChargesMinor,
      currency,
      informationalOnly: true,
    },
  ];
}

export function SettlementBreakdown({
  position,
}: {
  position: SettlementPosition;
}): React.JSX.Element {
  const lines = settlementLines(position);
  const deductions = lines.filter((line) => line.informationalOnly !== true);
  const informational = lines.filter((line) => line.informationalOnly === true);

  return (
    <div className="gc-stack" data-testid="settlement-breakdown">
      <div className="gc-stack gc-stack--tight">
        {deductions.map((line) => (
          <div key={line.label} className="gc-summary__row">
            <span>
              {line.label}
              <span className="gc-muted"> — {line.meaning}</span>
            </span>
            <span className="gc-summary__value gc-mono">
              {formatMoney({ minor: line.amountMinor, currency: line.currency })}
            </span>
          </div>
        ))}
      </div>

      <div className="gc-summary__row" data-testid="settlement-net">
        <span>
          <strong>Net before commercial terms</strong>
          <span className="gc-muted"> — what the ledger says CABS owes, before any deduction.</span>
        </span>
        <span className="gc-summary__value gc-mono">
          {/*
            The server's own figure, never `collected − refunded` computed here.
            The two must not be able to disagree, and only one of them is
            derived from postings that balance.
          */}
          {formatMoney({
            minor: position.netBeforeCommercialTermsMinor,
            currency: position.currency,
          })}
        </span>
      </div>

      {informational.map((line) => (
        <div key={line.label} className="gc-summary__row" data-testid="settlement-informational">
          <span>
            {line.label}
            <span className="gc-muted"> — {line.meaning}</span>
          </span>
          <span className="gc-summary__value gc-mono">
            {formatMoney({ minor: line.amountMinor, currency: line.currency })}
          </span>
        </div>
      ))}

      <Notice tone="info" title="Not a payout">
        {SETTLEMENT_NOT_A_PAYOUT}
      </Notice>

      <span className="gc-muted">As at {new Date(position.asOf).toLocaleString()}</span>
    </div>
  );
}
